//global Word
import React, { useState } from "react";
import {
  FontWeights,
  getTheme,
  IButtonStyles,
  IconButton,
  IIconProps,
  mergeStyleSets,
  Modal,
  TeachingBubble,
} from "@fluentui/react";
import * as uuid from "uuid";

import { useAppSelector } from "../../redux/hooks/hooks";
import { PrimaryButton } from "@fluentui/react/lib/Button";
import LinkExistingDocument from "../document/LinkExistingDocument";
import { fetchExecutedFormAndInsertAsync } from "../../redux/feature/cpm/cpm-slice";
import { CoObjectItem, DocumentPartSource } from "../interfaces/CoObject";
import { createElaborationRequestAsync } from "../../redux/feature/object/remote-elaborations-slice";
import { ElaborationItem, ElaborationRequest } from "../../api/object/RefreshObject";
import LoadingManager from "../../common/loading/LoadingManager";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import { selectCpmUsername } from "../../redux/feature/settings/configurations-slice";
import { EditDocument } from "../document/EditDocument";
import { DocumentParameters } from "../document/ParametersDocument";
import { fetchDocumentMetadata } from "../../api/document/DocumentsPart";
import { DocumentPartRelativePosition } from "../../office/WordDocumentPart";
import { fetchDocumentPath } from "../../api/document/Documents";
import { getCoObjectDocumentPartInclude } from "../../api/object/CoObjectGeneral";
import { DEFAULT_FOLDER_CREATION } from "../document/WithApiFileLocationProvider";
import { store } from "../../redux/store/store";
import { defaultFormatting } from "../../utils/constants/formats";
import { AdvancedDocumentMetadata } from "../../utils/helpers/DocumentAdvancedCustomXmlHelper";
import { mergeStyles } from "@fluentui/react/lib/Styling";
import { LinkNonTagetikDocumentPanel } from "../document/LinkNonTagetikDocument";
import { ButtonVariants } from "../../styles/fluent/ButtonVariants";

export const createCoObjectDocumentPart = async (
  documentId: string,
  linkDetail: { partDocumentId: string } | { documentDriveItemId: string } | undefined,
  name: string,
  description: string,
  path: string,
  referringPartId?: string,
  referringPartOrder?: number,
  relativePosition?: "before" | "after",
  advanced?: AdvancedDocumentMetadata,
  username?: string,
  parameters?: DocumentParameters[],
  multilinked?: boolean
) => {
  let position = undefined;
  if (documentId !== referringPartId && referringPartOrder) {
    const coObjectDocumentPartInclude = await getCoObjectDocumentPartInclude(
      documentId,
      referringPartId,
      referringPartOrder
    );

    const contentControlId = parseInt(coObjectDocumentPartInclude.contentControlId);
    position = {
      contentControlId,
      relativePosition,
      contentControlOrder: referringPartOrder,
    };
  }
  let documentPartObject = await fetchExecutedFormAndInsertAsync({
    userId: username,
    coObject: {
      documentId: documentId,
      info: { objectName: name, objectType: "DOCUMENT_PART_INCLUDE", info: description },
      source: { includedDocumentPartId: (linkDetail as any)?.partDocumentId, path: path } as DocumentPartSource,
      parameters: parameters,
      advanced: advanced,
    },
    multilinked: multilinked,
    docPartIncludePosition: position,
    linkDetail,
  });
  if (linkDetail) {
    const clientID = store.getState().configurationState.clientId;
    const correlationID = uuid.v4();
    let elaborationItem: ElaborationItem = {
      correlationId: correlationID,
      contentControlId: documentPartObject.contentControlId,
      dbId: null,
      formId: null,
      sheet: null,
      area: null,
      objectId: name,
      objectType: "DOCUMENT_PART_INCLUDE",
      documentPartIncludedId: documentPartObject.source.includedDocumentPartId, // TODO: check model
      type: "REFRESH",
      updateOptions: { depthLevel: "SHALLOWEST" },
    };
    let elaborationRequest: ElaborationRequest = {
      correlationId: correlationID,
      clientId: clientID,
      documentId: documentId,
      userId: username,
      tgkElaborationItemList: [elaborationItem],
    };
    await createElaborationRequestAsync(elaborationRequest);
  }
  return documentPartObject;
};

const theme = getTheme();
const contentStyles = mergeStyleSets({
  header: [
    theme.fonts.xLarge,
    {
      flex: "1 1 auto",
      borderTop: `4px solid ${theme.palette.themePrimary}`,
      color: theme.palette.neutralPrimary,
      display: "flex",
      alignItems: "center",
      fontWeight: FontWeights.semibold,
      padding: "12px 12px 14px 0px",
    },
  ],
  body: {
    flex: "4 4 auto",
    padding: "0 24px 24px 24px",
    overflowY: "hidden",
    selectors: {
      p: { margin: "14px 0" },
      "p:first-child": { marginTop: 0 },
      "p:last-child": { marginBottom: 0 },
    },
  },
  container: {
    display: "flex",
    flexFlow: "column nowrap",
    alignItems: "stretch",
  },
  box: {
    width: "99%",
    height: "99%",
    position: "absolute",
    top: "0",
    left: "0",
    opacity: "0.6",
    zIndex: "1000",
    margin: "2px",
    backgroundColor: "gray",
  },
  pageContainer: {
    position: "relative",
  },

  contentWrap: {
    paddingBottom: "2.5rem",
    height: "100%",
    overflow: "auto",
  },

  content: {
    height: "100%",
  },
});

const iconButtonStyles: Partial<IButtonStyles> = {
  root: {
    color: theme.palette.neutralPrimary,
    marginTop: "4px",
  },
  rootHovered: {
    color: theme.palette.neutralDark,
  },
};

export interface CreateDocumentPartModalProps {
  fatherPartId: string;
  isFatherMultilinked: boolean;
  referringPart: { id: string; order: number | undefined } | undefined;
  relativePosition: DocumentPartRelativePosition["relativePosition"] | undefined;
  isModalOpen: boolean;
  onHide: (coObjectItem: CoObjectItem) => void;
  loadingManagerKey: string;
}

interface FromScratchWrapperState {
  status: "show-buttons" | "link" | "scratch" | "nontagetik";
}

const FromScratchWrapperInitialState: FromScratchWrapperState = {
  status: "show-buttons",
};

export const getPathForFileLocationExplorer = (address: string) => {
  const splitted = address.split("/").filter((p) => p.length);
  if (!splitted.length) return "";
  splitted.unshift(DEFAULT_FOLDER_CREATION);
  return splitted.join(">");
};

export const CreateDocumentPartModal: React.FunctionComponent<CreateDocumentPartModalProps> = (props) => {
  const { fatherPartId, isFatherMultilinked, referringPart, relativePosition, isModalOpen, onHide, loadingManagerKey } =
    props;
  const username = useAppSelector(selectCpmUsername); // TEMP waiting for real login

  const backIcon: IIconProps = { iconName: "ChevronLeftMed" };
  const [state, setState] = React.useState<FromScratchWrapperState>(FromScratchWrapperInitialState);
  const { status } = state;
  const [currentTeachingBubble, setTeachingBubble] = useState<"sharepoint" | "tagetik">(null);

  const sharepointId = "sharepointId";
  const tagetikId = "tagetikId";

  function resetAndHide(coObjectItem: CoObjectItem) {
    onHide(coObjectItem);
    setState({ ...state, status: "show-buttons" });
    LoadingManager.remove(loadingManagerKey);
  }

  const scrollableContentStyle = mergeStyleSets({
    scrollableContent: mergeStyles({
      maxHeight: status === "link" || status === "nontagetik" ? "calc(100vh - 62px)" : "unset",
    }),
  });

  const getTitle = () => {
    if (status === "show-buttons") return formatMessage("cof.createdocument.newdocumentpart");
    if (status === "scratch") return formatMessage("cof.objectsource.documentpart.createfromscratch");
    if (status === "link") return formatMessage("cof.objectsource.documentpart.createfromexisting");
    return formatMessage("cof.objectsource.documentpart.createfromnontagetik");
  };

  return (
    <Modal
      titleAriaId={"titleId"}
      isOpen={isModalOpen}
      onDismiss={() => resetAndHide(null)}
      isBlocking={false}
      scrollableContentClassName={scrollableContentStyle.scrollableContent}
      containerClassName={contentStyles.container}
      styles={{
        main: {
          minHeight: "100%",
          minWidth: "100%",
        },
      }}
    >
      <div className={contentStyles.pageContainer}>
        <div className={contentStyles.contentWrap}>
          <div className={contentStyles.content}>
            <div className={contentStyles.header}>
              <IconButton
                styles={iconButtonStyles}
                iconProps={backIcon}
                ariaLabel="Close popup modal"
                onClick={() => resetAndHide(null)}
              />
              <span id={"titleId"}>{getTitle()}</span>
            </div>
            <div
              style={{
                borderBottom: "0.15rem solid",
                color: "var(--color-blue)",
                width: "100%",
              }}
            />
            {status === "show-buttons" && (
              <div
                style={{
                  width: "100vw",
                  height: "calc(100vh - 5rem)",
                  objectFit: "contain",
                  position: "fixed",
                  bottom: 0,
                }}
              >
                <img
                  src={"/assets/newDocumentPart.svg"}
                  alt="new document part logo"
                  style={{
                    minWidth: "120px",
                    width: "calc(100vw - 4rem)",
                    height: "70vh",
                    objectFit: "contain",
                    position: "fixed",
                    top: "45%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    pointerEvents: "none",
                  }}
                />
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    position: "fixed",
                    bottom: 0,
                    width: "100%",
                    paddingBottom: "2rem",
                  }}
                >
                  <span
                    style={{
                      textAlign: "center",
                      color: "var(--color-blue)",
                      padding: "0 1.25rem",
                      marginBottom: "4px",
                    }}
                  >
                    {formatMessage("cof.createdocument.create.message.fromscratch")}
                  </span>
                  <PrimaryButton
                    id="from_scratch"
                    text={formatMessage("cof.createdocument.fromscratch")}
                    onClick={() => setState({ ...state, status: "scratch" })}
                    data-testid="scratch"
                    style={{ margin: "0 auto" }}
                    className={ButtonVariants.primary}
                  />
                  <span
                    style={{
                      textAlign: "center",
                      color: "var(--color-blue)",
                      padding: "0 1.25rem",
                      marginTop: "12px",
                      marginBottom: "4px",
                    }}
                  >
                    {formatMessage("cof.createdocument.create.message.fromexisting")}
                  </span>

                  <div
                    style={{
                      display: "flex",
                      gap: "4px",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto",
                      padding: "0 8px",
                    }}
                  >
                    {currentTeachingBubble === "tagetik" && (
                      <TeachingBubble
                        calloutProps={{ calloutWidth: 300, backgroundColor: "lightgray" }}
                        target={`#${tagetikId}`}
                        hasCloseButton={true}
                        closeButtonAriaLabel="Close"
                        onDismiss={() => setTeachingBubble(null)}
                        headline={"Tagetik"}
                      >
                        <text>{formatMessage("cof.createnewdocument.fromtagetik")}</text>
                      </TeachingBubble>
                    )}
                    <IconButton
                      id={tagetikId}
                      aria-label="Info"
                      iconProps={{ iconName: "InfoSolid", style: { fontSize: "larger" } }}
                      onClick={() => setTeachingBubble("tagetik")}
                    />
                    <PrimaryButton
                      id="from_tagetik"
                      text={formatMessage("cof.createnewpart.fromexisting")}
                      onClick={() => setState({ ...state, status: "link" })}
                      className={ButtonVariants.white}
                    />
                    <PrimaryButton
                      id="from_sharepoint"
                      text={formatMessage("cof.createnewpart.fromnontagetik")}
                      onClick={() => setState({ ...state, status: "nontagetik" })}
                      className={ButtonVariants.white}
                    />
                    <IconButton
                      id={sharepointId}
                      aria-label="Info"
                      iconProps={{ iconName: "InfoSolid", style: { fontSize: "larger" } }}
                      onClick={() => setTeachingBubble("sharepoint")}
                    />
                    {currentTeachingBubble === "sharepoint" && (
                      <TeachingBubble
                        calloutProps={{ calloutWidth: 300, backgroundColor: "lightgray" }}
                        target={`#${sharepointId}`}
                        hasCloseButton={true}
                        closeButtonAriaLabel="Close"
                        onDismiss={() => setTeachingBubble(null)}
                        headline={"Sharepoint"}
                      >
                        <text>{formatMessage("cof.createnewdocument.fromsharepoint")}</text>
                      </TeachingBubble>
                    )}
                  </div>
                </div>
              </div>
            )}
            {status === "scratch" && (
              <div data-testid="edit-document">
                <EditDocument
                  fatherPartId={fatherPartId}
                  isEdit={false}
                  onLastBackButton={undefined}
                  onSave={(input) =>
                    createCoObjectDocumentPart(
                      fatherPartId,
                      null,
                      input.name,
                      input.description,
                      input.path,
                      referringPart.id,
                      referringPart.order,
                      relativePosition,
                      input.advanced,
                      username,
                      input.parameters,
                      isFatherMultilinked
                    ).then((documentPartObject) => resetAndHide(documentPartObject))
                  }
                  getDocumentPartDetails={async () => {
                    const fatherMetadata = await fetchDocumentMetadata(props.fatherPartId);
                    const address = (await fetchDocumentPath(props.fatherPartId)) ?? "";
                    const path = getPathForFileLocationExplorer(address);
                    return {
                      documentData: {
                        description: undefined,
                        name: undefined,
                        root: false,
                        multilinked: isFatherMultilinked,
                        path,
                        documentId: undefined,
                        advanced: defaultFormatting,
                        language: null,
                        parameters: [],
                      },
                      fatherDocumentParameters: isFatherMultilinked ? null : fatherMetadata.parametersMetadata,
                    };
                  }}
                />
              </div>
            )}
            {status === "link" && (
              <div data-testid="link-existing-document">
                <LinkExistingDocument
                  onLink={(partIdToLink) =>
                    createCoObjectDocumentPart(
                      fatherPartId,
                      { partDocumentId: partIdToLink },
                      "",
                      "",
                      null,
                      referringPart.id,
                      referringPart.order,
                      relativePosition,
                      null,
                      username,
                      null
                    ).then((documentPartObject) => resetAndHide(documentPartObject))
                  }
                />
              </div>
            )}
            {status === "nontagetik" && (
              <div data-testid="link-nontagetik-document">
                <LinkNonTagetikDocumentPanel
                  fatherPartId={fatherPartId}
                  isFatherMultilinked={isFatherMultilinked}
                  onLink={(documentData, driveItemId) =>
                    createCoObjectDocumentPart(
                      fatherPartId,
                      { documentDriveItemId: driveItemId },
                      documentData.name,
                      documentData.description,
                      documentData.path,
                      referringPart.id,
                      referringPart.order,
                      relativePosition,
                      documentData.advanced,
                      username,
                      documentData.parameters,
                      true
                    ).then((documentPartObject) => resetAndHide(documentPartObject))
                  }
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
